

interface ConnectionTypeOptions {
  name: ConnectionTypes;
  identifier: string;
}

type ConnectionTypes = 'googleApps' | 'usernamePassword';

interface OktaAuthConfig {
  issuer: string;
  redirectUri: string;
  logoutUri?: string;
}

interface AuthConfig {
  domain: string;
  audience: string;
  clientId: string;
  okta?: OktaAuthConfig; // Optional Okta-specific config
  connections: {
    default: ConnectionTypeOptions;
    cedant: ConnectionTypeOptions;
  };
  useRefreshTokens: boolean;
  cacheLocation: 'localStorage' | 'sessionStorage' | 'memory';
}

interface Envs {
  [env: string]: {
    api: {
      host: string;
      paths: {
        graphql: string;
        downloadPiiReport: string;
        fileDownload: string;
        folderDownload: string;
        fileUpload: string;
        enableMockServiceWorker: boolean;
      };
    };
    auth: AuthConfig;
    sentry?: {
      dsn: string;
    };
    app: {
      defaults?: {
        paginationLimit: number;
        pollIntervalOverride?: number;
      };
      links: {
        vendia: string;
      };
    };
  };
}

const DEV_OKTA_DOMAIN        = 'https://treaty-auth-dev.resolutionlife.com';
const DEV_API_URL            = 'https://rl1api.dev.resolutionlife.com/v1/treaty';
const DEV_OKTA_ISSUER        = 'https://treaty-auth-dev.resolutionlife.com/oauth2/ausmzktodcbuTqVXQ1d7';
const DEV_OKTA_CLIENT_ID     = '0oan8bpqswv9v79eH1d7';
const DEV_OKTA_REDIRECT_URI  = 'https://treaty.dev.resolutionlife.com/callback';
const DEV_OKTA_LOGOUT_URI    = 'https://treaty.dev.resolutionlife.com';
const DEV_OKTA_AUDIENCE      = 'https://rl1api.dev.resolutionlife.com/v1/treaty';

const TEST_OKTA_DOMAIN       = 'https://treaty-auth-test.resolutionlife.com';
const TEST_API_URL           = 'https://rl1api.test.resolutionlife.com/v1/treaty';
const TEST_OKTA_ISSUER       = 'https://treaty-auth-test.resolutionlife.com/oauth2/auso6x6d48AMLaNnu1d7';
const TEST_OKTA_CLIENT_ID    = '0oao6xmxgfLctXFJ51d7';
const TEST_OKTA_REDIRECT_URI = 'https://treaty.test.resolutionlife.com/callback';
const TEST_OKTA_LOGOUT_URI   = 'https://treaty.test.resolutionlife.com';
const TEST_OKTA_AUDIENCE     = 'https://rl1api.test.resolutionlife.com/v1/treaty';

const PREPROD_OKTA_DOMAIN       = 'https://treaty-auth-preprod.resolutionlife.com';
const PREPROD_API_URL           = 'https://rl1api.preprod.resolutionlife.com/v1/treaty';
const PREPROD_OKTA_ISSUER       = 'https://treaty-auth-preprod.resolutionlife.com/oauth2/ausoxrb8ncooezvrk1d7';
const PREPROD_OKTA_CLIENT_ID    = '0oapy214zkpLaMxws1d7';
const PREPROD_OKTA_REDIRECT_URI = 'https://treaty.preprod.resolutionlife.com/callback';
const PREPROD_OKTA_LOGOUT_URI   = 'https://treaty.preprod.resolutionlife.com';
const PREPROD_OKTA_AUDIENCE     = 'https://rl1api.preprod.resolutionlife.com/v1/treaty';

const PROD_OKTA_DOMAIN       = 'https://treaty-auth.resolutionlife.com';
const PROD_API_URL           = 'https://rl1api.resolutionlife.com/v1/treaty';
const PROD_OKTA_ISSUER       = 'https://treaty-auth.resolutionlife.com/oauth2/aus1ywhatnt1NjLjv1d8';
const PROD_OKTA_CLIENT_ID    = '0oa1ywhakctGNMnwg1d8';
const PROD_OKTA_REDIRECT_URI = 'https://treaty.resolutionlife.com/callback';
const PROD_OKTA_LOGOUT_URI   = 'https://treaty.resolutionlife.com';
const PROD_OKTA_AUDIENCE     = 'https://rl1api.resolutionlife.com/v1/treaty';


export const envs: Envs = {
  local: {
    api: {
      host: DEV_API_URL,
      paths: {
        graphql: '/graphql',
        downloadPiiReport: '/pii/audit_report.csv',
        fileDownload: '/file/download',
        folderDownload: '/file/download-folder',
        fileUpload: '/file/upload',
        enableMockServiceWorker: false,
      },
    },
    auth: {
      domain: DEV_OKTA_DOMAIN,
      audience: DEV_OKTA_AUDIENCE,
      clientId: DEV_OKTA_CLIENT_ID,
      okta: {
        issuer: DEV_OKTA_ISSUER,
        redirectUri: 'http://localhost:3000/callback',
        logoutUri: 'http://localhost:3000',
      },
      connections: {
        default: { name: 'googleApps', identifier: 'resolutionlife-dev' },
        cedant: {
          name: 'usernamePassword',
          identifier: 'Cedant-dev-Username-Password-Authentication',
        },
      },
      useRefreshTokens: true,
      cacheLocation: 'localStorage',
    },
    app: {
      defaults: {
        paginationLimit: 50,
        pollIntervalOverride: 10000,
      },
      links: {
        vendia: 'https://share.vendia.net/uni',
      },
    },
  },
  msw: {
    api: {
      host: DEV_API_URL,
      paths: {
        graphql: '/graphql',
        downloadPiiReport: '/pii/audit_report.csv',
        fileDownload: '/file/download',
        folderDownload: '/file/download-folder',
        fileUpload: '/file/upload',
        enableMockServiceWorker: true,
      },
    },
    auth: {
      domain: DEV_OKTA_DOMAIN,
      audience: DEV_OKTA_AUDIENCE,
      clientId: DEV_OKTA_CLIENT_ID,
      okta: {
        issuer: DEV_OKTA_ISSUER,
        redirectUri: DEV_OKTA_REDIRECT_URI, // Updated
        logoutUri: DEV_OKTA_LOGOUT_URI,
      },
      connections: {
        default: { name: 'googleApps', identifier: 'resolutionlife-dev' },
        cedant: {
          name: 'usernamePassword',
          identifier: 'Cedant-dev-Username-Password-Authentication',
        },
      },
      useRefreshTokens: true,
      cacheLocation: 'localStorage',
    },
    app: {
      links: {
        vendia: 'https://share.vendia.net/uni',
      },
    },
  },
  development: {
    api: {
      host: DEV_API_URL,
      paths: {
        graphql: '/graphql',
        downloadPiiReport: '/pii/audit_report.csv',
        fileDownload: '/file/download',
        folderDownload: '/file/download-folder',
        fileUpload: '/file/upload',
        enableMockServiceWorker: false,
      },
    },
    auth: {
      domain: DEV_OKTA_DOMAIN,
      audience: DEV_OKTA_AUDIENCE,
      clientId: DEV_OKTA_CLIENT_ID,
      okta: {
        issuer: DEV_OKTA_ISSUER,
        redirectUri: DEV_OKTA_REDIRECT_URI,
        logoutUri: DEV_OKTA_LOGOUT_URI,
      },
      connections: {
        default: { name: 'googleApps', identifier: 'resolutionlife-dev' },
        cedant: {
          name: 'usernamePassword',
          identifier: 'Cedant-dev-Username-Password-Authentication',
        },
      },
      useRefreshTokens: true,
      cacheLocation: 'localStorage',
    },
    app: {
      defaults: {
        paginationLimit: 50,
        pollIntervalOverride: 10000,
      },
      links: {
        vendia: 'https://share.vendia.net/uni',
      },
    },
  },
  test: {
    api: {
      host: TEST_API_URL,
      paths: {
        graphql: '/graphql',
        downloadPiiReport: '/pii/audit_report.csv',
        fileDownload: '/file/download',
        folderDownload: '/file/download-folder',
        fileUpload: '/file/upload',
        enableMockServiceWorker: false,
      },
    },
    auth: {
      domain: TEST_OKTA_DOMAIN,
      audience: TEST_OKTA_AUDIENCE,
      clientId: TEST_OKTA_CLIENT_ID,
      okta: {
        issuer: TEST_OKTA_ISSUER,
        redirectUri: TEST_OKTA_REDIRECT_URI,
        logoutUri: TEST_OKTA_LOGOUT_URI,
      },
      connections: {
        default: { name: 'googleApps', identifier: 'resolutionlife-dev' },
        cedant: {
          name: 'usernamePassword',
          identifier: 'Cedant-dev-Username-Password-Authentication',
        },
      },
      useRefreshTokens: true,
      cacheLocation: 'localStorage',
    },
    app: {
      links: {
        vendia: 'https://share.vendia.net/uni',
      },
    },
  },
  preprod: {
    api: {
      host: PREPROD_API_URL,
      paths: {
        graphql: '/graphql',
        downloadPiiReport: '/pii/audit_report.csv',
        fileDownload: '/file/download',
        folderDownload: '/file/download-folder',
        fileUpload: '/file/upload',
        enableMockServiceWorker: false,
      },
    },
    auth: {
      domain: PREPROD_OKTA_DOMAIN,
      audience: PREPROD_OKTA_AUDIENCE,
      clientId: PREPROD_OKTA_CLIENT_ID,
      okta: {
        issuer: PREPROD_OKTA_ISSUER,
        redirectUri: PREPROD_OKTA_REDIRECT_URI,
        logoutUri: PREPROD_OKTA_LOGOUT_URI,
      },
      connections: {
        default: { name: 'googleApps', identifier: 'resolutionlife-dev' },
        cedant: {
          name: 'usernamePassword',
          identifier: 'Cedant-dev-Username-Password-Authentication',
        },
      },
      useRefreshTokens: true,
      cacheLocation: 'localStorage',
    },
    app: {
      links: {
        vendia: 'https://share.vendia.net/uni',
      },
    },
  },
  production: {
    api: {
      host: PROD_API_URL,
      paths: {
        graphql: '/graphql',
        downloadPiiReport: '/pii/audit_report.csv',
        fileDownload: '/file/download',
        folderDownload: '/file/download-folder',
        fileUpload: '/file/upload',
        enableMockServiceWorker: false,
      },
    },
    auth: {
      domain: PROD_OKTA_DOMAIN,
      audience: PROD_OKTA_AUDIENCE,
      clientId: PROD_OKTA_CLIENT_ID,
      okta: {
        issuer: PROD_OKTA_ISSUER,
        redirectUri: PROD_OKTA_REDIRECT_URI,
        logoutUri: PROD_OKTA_LOGOUT_URI,
      },
      connections: {
        default: { name: 'googleApps', identifier: 'resolutionlife-dev' },
        cedant: {
          name: 'usernamePassword',
          identifier: 'Cedant-dev-Username-Password-Authentication',
        },
      },
      useRefreshTokens: true,
      cacheLocation: 'localStorage',
    },
    app: {
      links: {
        vendia: 'https://share.vendia.net/uni',
      },
    },
  },
  e2e: {
    api: {
      host: 'http://localhost:9000',
      paths: {
        graphql: '/graphql',
        downloadPiiReport: '/pii/audit_report.csv',
        fileDownload: '/file/download',
        folderDownload: '/file/download-folder',
        fileUpload: '/file/upload',
        enableMockServiceWorker: false,
      },
    },
    auth: {
      domain: DEV_OKTA_DOMAIN,
      audience: DEV_OKTA_AUDIENCE,
      clientId: DEV_OKTA_CLIENT_ID,
      okta: {
        issuer: DEV_OKTA_ISSUER,
        redirectUri: 'http://localhost:4000/callback',
        logoutUri: 'http://localhost:4000',
      },
      connections: {
        default: { name: 'googleApps', identifier: 'resolutionlife-dev' },
        cedant: {
          name: 'usernamePassword',
          identifier: 'Cedant-dev-Username-Password-Authentication',
        },
      },
      useRefreshTokens: true,
      cacheLocation: 'localStorage',
    },
    app: {
      links: {
        vendia: 'https://share.vendia.net/uni',
      },
    },
  },
};

export const APP_VERSION = process.env.REACT_APP_VERSION;
export const APP_GIT_SHA = process.env.REACT_APP_GIT_SHA;

const selectedEnv = process.env.REACT_APP_ENV || 'development';
const config = envs[selectedEnv];

export default config;

// Warn if environment is not set or invalid
if (!process.env.REACT_APP_ENV || !envs[process.env.REACT_APP_ENV]) {
  console.warn('REACT_APP_ENV is not set or invalid, falling back to "development"');
}
